/**
 * ConfigPlantillasWhatsApp — Editor de plantillas WhatsApp del equipo comercial.
 * Lista + CRUD + preview live.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Chip, Switch, Stack,
  Paper, CircularProgress, IconButton, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import useToast from './common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => {
  try {
    const ea = localStorage.getItem('empresa_activa');
    if (ea) return ea;
    return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1';
  } catch { return '1'; }
};
const getUserId = () => {
  const direct = localStorage.getItem('user_id');
  if (direct) return direct;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.id || u.user_id || u._id || null;
  } catch { return null; }
};
const getAuthHeaders = () => {
  const t = localStorage.getItem('token');
  const uid = getUserId();
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    ...(uid ? { 'X-User-Id': String(uid) } : {}),
  };
};

const VARIABLES_SAMPLE = {
  nombre_contacto: 'María',
  comercial_nombre: 'Carlos',
  empresa: 'Entretur',
};

function renderizar(t, vars) {
  let out = t || '';
  for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(v || '');
  return out.replace(/\{[a-z_][a-z0-9_]*\}/g, '');
}

export default function ConfigPlantillasWhatsApp() {
  const { toast, ToastContainer } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);  // plantilla seleccionada para editar
  const [editado, setEditado] = useState({});  // { id: { cuerpo, activa, ... } }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/comercial/plantillas-whatsapp?empresa_id=${getEmpresaId()}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setItems(d.plantillas || []);
      if (!sel && d.plantillas?.length) setSel(d.plantillas[0]);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [sel]); // eslint-disable-line
  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const valorEditado = sel && editado[sel.id]
    ? editado[sel.id]
    : sel ? {
        nombre: sel.nombre, descripcion: sel.descripcion || '',
        cuerpo: sel.cuerpo, aplica_a: sel.aplica_a, activa: sel.activa,
      } : null;
  const dirty = sel && editado[sel.id] !== undefined;

  const handleEditar = (campo, val) => {
    if (!sel) return;
    setEditado(prev => ({
      ...prev,
      [sel.id]: { ...valorEditado, [campo]: val },
    }));
  };

  const handleGuardar = async () => {
    if (!sel || !dirty) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/comercial/plantillas-whatsapp/${sel.id}`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({
          nombre: valorEditado.nombre, descripcion: valorEditado.descripcion,
          cuerpo: valorEditado.cuerpo, aplica_a: valorEditado.aplica_a,
          activa: valorEditado.activa,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success('Plantilla guardada');
      setEditado(prev => { const n = { ...prev }; delete n[sel.id]; return n; });
      await cargar();
    } catch (e) { toast.error(e.message); }
  };

  const handleNueva = async () => {
    try {
      const body = {
        empresa_id: getEmpresaId(),
        nombre: 'Nueva plantilla',
        descripcion: '',
        cuerpo: 'Hola {nombre_contacto}, te escribo de {empresa}.',
        aplica_a: 'ambos',
        orden: items.length,
      };
      const r = await fetch(`${API_BASE}/turismo/comercial/plantillas-whatsapp`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      await cargar();
      toast.success('Plantilla creada');
    } catch (e) { toast.error(e.message); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/comercial/plantillas-whatsapp/${id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      setSel(null);
      await cargar();
      toast.success('Plantilla eliminada');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Alert severity="info" icon={<WhatsAppIcon />} sx={{ mb: 2, fontSize: 13 }}>
        Plantillas de mensaje que el equipo comercial puede enviar a agencias y prospectos con un click.
        Variables: <code>{'{nombre_contacto}'}</code>, <code>{'{comercial_nombre}'}</code>, <code>{'{empresa}'}</code>.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2 }}>
        {/* Lista lateral */}
        <Box>
          <Button fullWidth variant="contained" disableElevation startIcon={<AddIcon />}
            onClick={handleNueva}
            sx={{ textTransform: 'none', mb: 1.5,
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            Nueva plantilla
          </Button>
          <Stack spacing={0.5}>
            {items.map(p => (
              <Paper key={p.id} onClick={() => setSel(p)}
                sx={{
                  p: 1.25, borderRadius: 2, cursor: 'pointer',
                  border: '1px solid',
                  borderColor: sel?.id === p.id ? '#25D366' : '#e2e8f0',
                  bgcolor: sel?.id === p.id ? '#dcfce7' : '#fff',
                  '&:hover': { borderColor: '#25D366' },
                  opacity: p.activa ? 1 : 0.6,
                }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>
                    {p.nombre}
                  </Typography>
                  {!p.activa && (
                    <Chip size="small" label="inactiva" sx={{ height: 16, fontSize: 9 }} />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  Aplica a: {p.aplica_a}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>

        {/* Editor + preview */}
        {sel && valorEditado ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>Editor</Typography>
                <Switch size="small" checked={!!valorEditado.activa}
                  onChange={e => handleEditar('activa', e.target.checked)} />
                <IconButton size="small" onClick={() => handleEliminar(sel.id)}>
                  <DeleteIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                </IconButton>
              </Stack>

              <Stack spacing={1.5}>
                <TextField size="small" fullWidth label="Nombre"
                  value={valorEditado.nombre}
                  onChange={e => handleEditar('nombre', e.target.value)} />
                <TextField size="small" fullWidth label="Descripción (opcional)"
                  value={valorEditado.descripcion}
                  onChange={e => handleEditar('descripcion', e.target.value)} />
                <TextField select size="small" fullWidth label="Aplica a"
                  value={valorEditado.aplica_a}
                  onChange={e => handleEditar('aplica_a', e.target.value)}>
                  <MenuItem value="ambos">Ambos (agencia y prospecto)</MenuItem>
                  <MenuItem value="agencia">Solo agencias</MenuItem>
                  <MenuItem value="prospecto">Solo prospectos</MenuItem>
                </TextField>
                <TextField multiline minRows={8} fullWidth label="Mensaje"
                  value={valorEditado.cuerpo}
                  onChange={e => handleEditar('cuerpo', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13 } }} />
              </Stack>

              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase',
                                   fontWeight: 700, mb: 0.5 }}>
                  Variables (clic para insertar)
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {Object.keys(VARIABLES_SAMPLE).map(v => (
                    <Chip key={v} size="small" label={`{${v}}`}
                      onClick={() => handleEditar('cuerpo', valorEditado.cuerpo + `{${v}}`)}
                      sx={{ fontSize: 10, fontFamily: 'monospace', cursor: 'pointer',
                            bgcolor: '#f1f5f9', color: '#0d9488' }} />
                  ))}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" disableElevation startIcon={<SaveIcon />}
                  disabled={!dirty} onClick={handleGuardar}
                  sx={{ textTransform: 'none',
                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  Guardar
                </Button>
                {dirty && (
                  <Button onClick={() => {
                    setEditado(prev => { const n = { ...prev }; delete n[sel.id]; return n; });
                  }} sx={{ textTransform: 'none' }}>
                    Descartar
                  </Button>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                <VisibilityIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>
                  Vista previa (datos de ejemplo)
                </Typography>
              </Stack>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 2,
                          border: '1px solid #bbf7d0', minHeight: 200,
                          fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {renderizar(valorEditado.cuerpo, VARIABLES_SAMPLE)
                  || <em style={{ color: '#94a3b8' }}>(sin contenido)</em>}
              </Box>
              <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 1 }}>
                Reemplazado con: nombre_contacto=María, comercial_nombre=Carlos, empresa=Entretur.
              </Typography>
            </Paper>
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
              Selecciona una plantilla a la izquierda o crea una nueva.
            </Typography>
          </Paper>
        )}
      </Box>

      <ToastContainer />
    </Box>
  );
}
